/** Calendar helpers. A "day" is an ISO date string (YYYY-MM-DD) in Kazakhstan time (UTC+5),
 *  matching the default of xp_events.day on the server. */

export type IsoDay = string;

export const MS_PER_DAY = 86_400_000;

/**
 * Kazakhstan uses a single zone, UTC+5, without daylight saving (since March 2024). The
 * offset is fixed here rather than read from the device's tz database: devices with tzdata
 * older than 2024a still put Almaty at UTC+6 and would shift late-evening XP to the next day.
 */
const APP_UTC_OFFSET_HOURS = 5;
const APP_UTC_OFFSET = '+05:00';

const shifted = (date: Date) => new Date(date.getTime() + APP_UTC_OFFSET_HOURS * 3_600_000);

export function appDay(date: Date): IsoDay {
  return shifted(date).toISOString().slice(0, 10);
}

export function appHour(date: Date): number {
  return shifted(date).getUTCHours();
}

/** Milliseconds until the next app day starts (for midnight rollovers). */
export function msUntilNextDay(date: Date): number {
  const next = new Date(`${addDays(appDay(date), 1)}T00:00:00${APP_UTC_OFFSET}`);
  return next.getTime() - date.getTime();
}

function dayToUtcMs(day: IsoDay): number {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return Date.UTC(y, m - 1, d);
}

export function addDays(day: IsoDay, delta: number): IsoDay {
  return new Date(dayToUtcMs(day) + delta * MS_PER_DAY).toISOString().slice(0, 10);
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: IsoDay, to: IsoDay): number {
  return Math.round((dayToUtcMs(to) - dayToUtcMs(from)) / MS_PER_DAY);
}

/** Monday of the ISO week that contains `day` (same rule as Postgres date_trunc('week')). */
export function weekStart(day: IsoDay): IsoDay {
  const weekday = new Date(dayToUtcMs(day)).getUTCDay(); // 0 = Sunday
  return addDays(day, -((weekday + 6) % 7));
}

export function greetingFor(date: Date): string {
  const hour = appHour(date);
  if (hour >= 5 && hour < 12) return 'Доброе утро';
  if (hour >= 12 && hour < 17) return 'Добрый день';
  if (hour >= 17 && hour < 23) return 'Добрый вечер';
  return 'Доброй ночи';
}

/** The instant an app day begins, as an ISO timestamp for database range filters. */
export function dayStartIso(day: IsoDay): string {
  return new Date(`${day}T00:00:00${APP_UTC_OFFSET}`).toISOString();
}
